import React, { useState, useEffect } from 'react';
import { Shield, Save, Download, Lock } from 'lucide-react';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const PrivacySettings = ({ userProfile, onSave, saving }) => {
  const [privacySettings, setPrivacySettings] = useState({
    profile_visibility: 'private', // public, private, friends
    allow_contact: false,
    data_sharing: false,
    analytics_tracking: true,
    show_activity: false,
    allow_indexing: false
  });

  useEffect(() => {
    // In a real app, fetch user's privacy settings
    // For now, using defaults
  }, [userProfile]);

  const handleSettingChange = (setting, value) => {
    setPrivacySettings(prev => ({ ...prev, [setting]: value }));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    onSave({ privacy_settings: privacySettings });
  };

  const handleDataExport = () => {
    // In a real app, this would trigger a data export process
    alert('Data export request submitted. You will receive an email with your data within 24 hours.');
  };

  const PrivacyOption = ({ title, description, children }) => (
    <div className="space-y-3">
      <div>
        <h3 className="text-base font-medium text-gray-900">{title}</h3>
        <p className="text-sm text-gray-600">{description}</p>
      </div>
      <div className="pl-4">
        {children}
      </div>
    </div>
  );

  const CheckboxOption = ({ label, description, checked, onChange }) => (
    <div className="flex items-start space-x-3">
      <Checkbox
        checked={checked}
        onCheckedChange={onChange}
        className="mt-1"
      />
      <div className="flex-1">
        <label className="text-sm font-medium text-gray-900 cursor-pointer">
          {label}
        </label>
        {description && (
          <p className="text-sm text-gray-600 mt-1">{description}</p>
        )}
      </div>
    </div>
  );

  return (
    <div className="p-6">
      <div className="border-b border-gray-200 pb-4 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 flex items-center">
          <Shield className="h-5 w-5 mr-2" />
          Privacy Settings
        </h2>
        <p className="text-gray-600 mt-1">
          Control how your information is shared and used
        </p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Profile Visibility */}
        <PrivacyOption
          title="Profile Visibility"
          description="Control who can see your profile information"
        >
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <input
                type="radio"
                id="public"
                name="profile_visibility"
                value="public"
                checked={privacySettings?.profile_visibility === 'public'}
                onChange={(e) => handleSettingChange('profile_visibility', e?.target?.value)}
                className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
              />
              <label htmlFor="public" className="text-sm">
                <span className="font-medium text-gray-900">Public</span>
                <span className="text-gray-600 ml-2">Anyone can see your profile</span>
              </label>
            </div>
            
            <div className="flex items-center space-x-3">
              <input
                type="radio"
                id="private"
                name="profile_visibility"
                value="private"
                checked={privacySettings?.profile_visibility === 'private'}
                onChange={(e) => handleSettingChange('profile_visibility', e?.target?.value)}
                className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
              />
              <label htmlFor="private" className="text-sm">
                <span className="font-medium text-gray-900">Private</span>
                <span className="text-gray-600 ml-2">Only you can see your profile</span>
              </label>
            </div>
          </div>
        </PrivacyOption>

        {/* Contact Permissions */}
        <PrivacyOption
          title="Contact Permissions"
          description="Control how others can contact you"
        >
          <CheckboxOption
            label="Allow Contact from Other Users"
            description="Let other users send you messages through the platform"
            checked={privacySettings?.allow_contact}
            onChange={(checked) => handleSettingChange('allow_contact', checked)}
          />
        </PrivacyOption>

        {/* Data Usage */}
        <PrivacyOption
          title="Data Usage"
          description="Control how your data is used"
        >
          <div className="space-y-4">
            <CheckboxOption
              label="Share Usage Data"
              description="Help improve our service by sharing anonymous usage statistics"
              checked={privacySettings?.data_sharing}
              onChange={(checked) => handleSettingChange('data_sharing', checked)}
            />
            
            <CheckboxOption
              label="Analytics Tracking"
              description="Allow us to track your usage for analytics purposes"
              checked={privacySettings?.analytics_tracking}
              onChange={(checked) => handleSettingChange('analytics_tracking', checked)}
            />
          </div>
        </PrivacyOption>

        {/* Activity Visibility */}
        <PrivacyOption
          title="Activity Visibility"
          description="Control what activity information is visible to others"
        >
          <div className="space-y-4">
            <CheckboxOption
              label="Show Activity Status"
              description="Let others see when you're active on the platform"
              checked={privacySettings?.show_activity}
              onChange={(checked) => handleSettingChange('show_activity', checked)}
            />
            
            <CheckboxOption
              label="Allow Search Engine Indexing"
              description="Allow search engines to index your public profile"
              checked={privacySettings?.allow_indexing}
              onChange={(checked) => handleSettingChange('allow_indexing', checked)}
            />
          </div>
        </PrivacyOption>

        {/* Data Rights */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <h3 className="text-base font-medium text-gray-900 mb-4 flex items-center">
            <Lock className="h-5 w-5 mr-2" />
            Your Data Rights
          </h3>
          
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-gray-900">Request Your Data</h4>
              <p className="text-sm text-gray-600 mb-3">
                Download a copy of all the data we have about you
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleDataExport}
                className="flex items-center"
              >
                <Download className="h-4 w-4 mr-2" />
                Export My Data
              </Button>
            </div>

            <div className="border-t pt-4">
              <h4 className="text-sm font-medium text-gray-900">Data Retention</h4>
              <p className="text-sm text-gray-600">
                We retain your data as long as your account is active. You can delete your account
                at any time from the Account Management section.
              </p>
            </div>

            <div className="border-t pt-4">
              <h4 className="text-sm font-medium text-gray-900">Questions About Privacy</h4>
              <p className="text-sm text-gray-600">
                If you have questions about how we handle your data, please contact our 
                <a href="mailto:privacy@servicewatch.com" className="text-blue-600 hover:text-blue-800 ml-1">
                  privacy team
                </a>.
              </p>
            </div>
          </div>
        </div>

        <div className="pt-4 border-t border-gray-200">
          <Button
            type="submit"
            disabled={saving}
            className="flex items-center"
          >
            {saving ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {saving ? 'Saving...' : 'Save Privacy Settings'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default PrivacySettings;